// Top-level app component.
//
// Runs a three-stage bootstrap on mount:
//
//   1. Token exchange. App Bridge already has an id_token; we POST it to
//      /api/auth/token-exchange so the backend has a Store row + fresh
//      offline access token. This must succeed before any /api/* call.
//
//   2. Destinations fetch. GET /api/destinations. Used to decide between
//      the onboarding-wizard flow (zero destinations) and the
//      post-onboarding flows below.
//
//   3. Billing status fetch. GET /api/billing/status. Drives the
//      PlanPicker-vs-Dashboard gate once at least one destination is
//      configured.
//
// Routing gates (in order; resolveGate in gates.ts, unit-tested):
//   - /data-requests                          → <DataRequests/>  (review W29:
//                                               a legal duty, never gated)
//   - free period over, no plan               → <PlanPicker mode="ended"/>
//   - 0 destinations                          → <Onboarding/>
//   - ≥1 destination + no subscription        → <PlanPicker/>   (unless skipped)
//   - ≥1 destination + subscription present   → <Dashboard/>
//
// "Skipped" is a per-session local state: "Maybe later" on the picker
// reveals the dashboard for that session, and the picker comes back next
// session. That holds inside the 30 free days a store with no plan gets
// (review W31). After them delivery is paused and the end-of-period picker
// shows every session, ahead of onboarding (destination saves 402 by then).
// It is a soft gate: with a destination, "Not now" (its own session flag)
// opens the dashboard, where the paywall banner and the Activity feed's
// "Not sent" rows show the pause.
//
// Errors in any stage land the user on a red banner. No auto-retry —
// we'd rather the merchant see the failure and try again than silently
// spin forever. A future polish: distinguish "token-exchange failed"
// (re-install button) from "destinations fetch failed" (retry button).

import { useEffect, useRef, useState } from "react";
import { useAppBridge, NavMenu } from "@shopify/app-bridge-react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Page,
  Card,
  Text,
  BlockStack,
  Spinner,
  Banner,
} from "@shopify/polaris";
import { apiFetch, ApiError, getCached, EMBED_SHOP, shopScopedKey } from "./api";
import { Onboarding, type StoreContext } from "./Onboarding";
import type { DestinationType } from "./destination-catalog";
import { Dashboard } from "./Dashboard";
import { PlanPicker } from "./PlanPicker";
import { Billing } from "./Billing";
import { Destinations } from "./Destinations";
import { FeedbackWidget } from "./FeedbackWidget";
import { FreePeriodBanner } from "./FreePeriodBanner";
import { DataRequests } from "./DataRequests";
import { freePeriodEnded, type EntitlementView, type FreePeriodView } from "./billing-shared";
import { DATA_REQUESTS_PATH, resolveGate } from "./gates";

interface DestinationView {
  // DestinationType, not a hand-written union. A copy that stayed at six
  // while the API returned a seventh would not fail to compile: the type maps
  // keyed off it return undefined for the new one, and the merchant sees
  // "undefined is in test mode".
  type: DestinationType;
  enabled: boolean;
  configured: boolean;
  // This service's dry-run flag for the type (events logged, not sent).
  // Optional so preview/mocks don't have to set it; the API always sends it.
  dryRun?: boolean;
  lastSuccessAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// Mirrors the GET /api/billing/status response shape. Only the fields
// the gate cares about are typed here; full status detail is fetched
// again by <Billing/> on its own mount.
interface BillingStatusView {
  subscription: { plan: string; status: string } | null;
  // True when the shop is comped for the beta (full access, never charged).
  // When set, skip the plan picker entirely.
  comped?: boolean;
  // Shopify's hosted plan-selection URL (admin.shopify.com/.../pricing_plans).
  planSelectionUrl?: string;
  // Backend-evaluated entitlement, used for routing and paywall surfacing.
  // `blocked` is the backend's comp-aware gate (gatePaidFeature) so the
  // frontend doesn't have to recreate the policy.
  entitlement: EntitlementView;
  // The 30 free days of a store with no plan (review W31). Null when comped
  // or once any subscription row exists; absent from a pre-W31 backend.
  freePeriod?: FreePeriodView | null;
}

type BootstrapState =
  | {
      kind: "loading";
      phase: "token_exchange" | "destinations" | "billing" | "activating";
    }
  | {
      kind: "ready";
      shop: string;
      destinations: DestinationView[];
      // What this shop may connect, as the API reports it. Carried so the
      // dashboard's "N of M destinations connected" nudge counts the shop's
      // own list rather than the catalog, which includes types at release
      // "hidden" that no merchant can reach.
      availableTypes?: string[];
      storeContext: StoreContext;
      billing: BillingStatusView;
    }
  | { kind: "error"; message: string };

// Build the initial bootstrap state from the persisted SWR cache. App Bridge's
// NavMenu reloads the embedded iframe on every nav click, so without this each
// nav would re-show the connect spinner. When the cache has the last
// destinations + billing + shop, paint the previous view instantly; the
// bootstrap effect still runs token-exchange and revalidates in the background.
function seedBootstrapState(): BootstrapState {
  try {
    // Returning from Shopify's hosted plan page (?plan_handle=...): the
    // cached state predates the approval — it would flash the plan picker or
    // the OLD plan for the seconds the sync takes. Skip the cached paint and
    // hold an "Activating your plan" spinner until fresh status lands.
    if (new URLSearchParams(window.location.search).get("plan_handle") !== null) {
      return { kind: "loading", phase: "activating" };
    }
    const dest = getCached<{
      destinations: DestinationView[];
      storeContext: StoreContext;
      availableTypes?: string[];
    }>("/api/destinations");
    const billing = getCached<BillingStatusView>("/api/billing/status");
    const shop = sessionStorage.getItem("dc_shop");
    // Never seed another store's view (audit F2): the iframe's sessionStorage
    // survives an in-tab switch between stores. The cache itself is
    // shop-scoped now, but the dc_shop check keeps this belt-and-suspenders.
    if (shop && EMBED_SHOP !== "unknown-shop" && shop !== EMBED_SHOP) {
      return { kind: "loading", phase: "token_exchange" };
    }
    if (dest && billing && shop) {
      return {
        kind: "ready",
        shop,
        destinations: dest.destinations,
        availableTypes: dest.availableTypes,
        storeContext: dest.storeContext,
        billing,
      };
    }
  } catch {
    // sessionStorage blocked or cache miss — fall through to a cold start.
  }
  return { kind: "loading", phase: "token_exchange" };
}

export default function App() {
  const shopify = useAppBridge();
  // Seed from the persisted cache so a NavMenu reload re-paints the last view
  // instantly. hadSeed lets us avoid blanking to an error screen on a transient
  // background-refresh failure (we keep showing the cached view instead).
  const [state, setState] = useState<BootstrapState>(seedBootstrapState);
  const hadSeed = useRef(state.kind === "ready");
  // The cached view stays up when a background refresh fails, but it must say
  // so: silently showing last visit's "Healthy" after every refresh failed
  // hid real outages (review W21).
  const [refreshFailed, setRefreshFailed] = useState(false);
  // Skip for the PlanPicker, persisted in sessionStorage so a NavMenu reload
  // doesn't re-block every navigation with the picker. (The dev store has no
  // subscription, so the gate would otherwise fire on every page load.)
  // sessionStorage scope = once-per-session nudge: it resurfaces in a new
  // session, not on every in-session nav.
  const [pickerSkipped, setPickerSkippedState] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(shopScopedKey("dc_pickerSkipped")) === "1";
    } catch {
      return false;
    }
  });
  const setPickerSkipped = (v: boolean) => {
    setPickerSkippedState(v);
    try {
      if (v) sessionStorage.setItem(shopScopedKey("dc_pickerSkipped"), "1");
      else sessionStorage.removeItem(shopScopedKey("dc_pickerSkipped"));
    } catch {
      // storage blocked — falls back to in-memory only.
    }
  };
  // "Not now" on the end-of-period picker (review W31). A separate key, so a
  // "Maybe later" taken during the free period can't hide the picker that
  // says delivery has paused.
  const [pickerSkippedEnded, setPickerSkippedEndedState] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(shopScopedKey("dc_pickerSkippedEnded")) === "1";
    } catch {
      return false;
    }
  });
  const skipEndedPicker = () => {
    setPickerSkippedEndedState(true);
    try {
      sessionStorage.setItem(shopScopedKey("dc_pickerSkippedEnded"), "1");
    } catch {
      // storage blocked: in-memory only.
    }
  };
  // Merchant-facing navigation is URL-driven via react-router, so the App
  // Bridge NavMenu (which renders the app's links into Shopify admin's left
  // sidebar) can drive it. The bootstrap state above lives in this component,
  // which doesn't unmount on route changes, so it persists across nav.
  const location = useLocation();
  const navigate = useNavigate();
  const view: "dashboard" | "billing" | "destinations" =
    location.pathname === "/billing"
      ? "billing"
      : location.pathname === "/destinations"
        ? "destinations"
        : "dashboard";

  // Re-fetch destinations + billing after a child action (onboarding
  // finished, etc.) so we transition into the next phase without a full
  // page reload.
  const refresh = async (shop: string) => {
    const [destResp, billingResp] = await Promise.all([
      apiFetch<{
        destinations: DestinationView[];
        storeContext: StoreContext;
        availableTypes?: string[];
      }>(shopify, "/api/destinations"),
      apiFetch<BillingStatusView>(shopify, "/api/billing/status"),
    ]);
    setState({
      kind: "ready",
      shop,
      destinations: destResp.destinations,
      availableTypes: destResp.availableTypes,
      storeContext: destResp.storeContext,
      billing: billingResp,
    });
    setRefreshFailed(false);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Stage 1: token exchange. idToken() throws if App Bridge hasn't
        // booted; useAppBridge guarantees it has by the time we get here.
        const idToken = await shopify.idToken();
        const res = await fetch("/api/auth/token-exchange", {
          method: "POST",
          headers: { Authorization: `Bearer ${idToken}` },
        });
        if (!res.ok) {
          let detail = `${res.status} ${res.statusText}`;
          try {
            const body = (await res.json()) as { error?: string };
            if (body.error) detail = body.error;
          } catch {
            // non-JSON response; stick with status line
          }
          throw new Error(detail);
        }
        const body = (await res.json()) as { shop: string; installed: boolean };
        if (cancelled) return;

        // If the merchant just returned from Shopify's hosted plan page, it
        // appends ?plan_handle=...; force a Partner API sync so billing status
        // reflects the new plan before we read it below. The handle rides in
        // the body: it is the ONLY signal that distinguishes Flex (whose
        // Partner API item carries just the meter handle), so dropping it
        // would mirror the old plan forever after a switch to Flex. Best-effort.
        const planHandle = new URLSearchParams(window.location.search).get("plan_handle");
        if (planHandle !== null) {
          try {
            await apiFetch(shopify, "/api/billing/sync", {
              method: "POST",
              body: JSON.stringify({ plan_handle: planHandle }),
            });
          } catch {
            // non-fatal — the reconciler catches up; the status read still proceeds.
          }
          if (cancelled) return;
        }

        // Stages 2 + 3: destinations (onboarding gate) + billing (picker gate),
        // in parallel. No intermediate "loading" setState — on a cold start the
        // connect spinner stays up until ready; on a seeded reload the cached
        // view stays up and is replaced silently when the fresh data lands.
        const [destResp, billingResp0] = await Promise.all([
          apiFetch<{
            destinations: DestinationView[];
            storeContext: StoreContext;
            availableTypes?: string[];
          }>(shopify, "/api/destinations"),
          apiFetch<BillingStatusView>(shopify, "/api/billing/status"),
        ]);
        if (cancelled) return;

        // Post-approval settle loop (App Store review round 2): the merchant
        // just approved a plan (plan_handle in the URL) but billing still
        // reads no subscription — Shopify's reporting surface can lag the
        // approval by a few seconds. Re-showing the plan picker here reads as
        // "your approval didn't take". Retry the sync+status pair briefly
        // (the backend's Admin API fallback usually resolves it on the first
        // retry) while the boot spinner keeps showing; give up quietly after
        // a few attempts and let the picker render as before.
        let billingResp = billingResp0;
        // "Misaligned" covers both post-approval shapes: no subscription yet
        // (fresh pick) and the OLD plan still showing (a Standard→Pro change
        // the reporting surface hasn't caught up with).
        const expectedPlan =
          planHandle !== null && ["flex", "standard", "pro"].includes(planHandle)
            ? planHandle
            : null;
        const misaligned = (r: BillingStatusView) =>
          r.subscription === null ||
          (expectedPlan !== null && r.subscription.plan !== expectedPlan);
        if (planHandle !== null && !billingResp.comped && misaligned(billingResp)) {
          for (let attempt = 0; attempt < 3 && !cancelled; attempt++) {
            await new Promise((resolve) => setTimeout(resolve, 2000));
            try {
              await apiFetch(shopify, "/api/billing/sync", {
                method: "POST",
                body: JSON.stringify({ plan_handle: planHandle }),
              });
              billingResp = await apiFetch<BillingStatusView>(shopify, "/api/billing/status");
              if (!misaligned(billingResp)) break;
            } catch {
              // transient — next attempt or the fallback render handles it
            }
          }
        }
        if (cancelled) return;

        // Remember the shop so seedBootstrapState() can paint instantly next
        // reload (destinations + billing are already in the SWR cache).
        try {
          sessionStorage.setItem("dc_shop", body.shop);
        } catch {
          // storage blocked — seeding just won't kick in on the next reload.
        }
        setState({
          kind: "ready",
          shop: body.shop,
          destinations: destResp.destinations,
          availableTypes: destResp.availableTypes,
          storeContext: destResp.storeContext,
          billing: billingResp,
        });
        setRefreshFailed(false);
      } catch (err) {
        if (cancelled) return;
        // On a seeded reload, a transient refresh failure shouldn't blank the
        // cached view — keep showing it, flagged as saved data. Cold starts
        // surface the error.
        if (hadSeed.current) {
          console.warn(
            "Datacrow: background refresh failed; showing cached view",
            err,
          );
          setRefreshFailed(true);
          return;
        }
        const message =
          err instanceof ApiError
            ? `${err.message} (HTTP ${err.status})`
            : (err as Error).message || "unknown error";
        setState({ kind: "error", message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shopify]);

  if (state.kind === "loading") {
    const label =
      state.phase === "token_exchange"
        ? "Connecting to Datacrow backend…"
        : state.phase === "destinations"
          ? "Loading your setup…"
          : state.phase === "activating"
            ? "Activating your plan…"
            : "Checking subscription…";
    return (
      <Page title="Datacrow">
        <Card>
          <BlockStack gap="200" align="center">
            <Spinner accessibilityLabel="Loading" size="large" />
            <Text as="p" tone="subdued">
              {label}
            </Text>
          </BlockStack>
        </Card>
      </Page>
    );
  }

  if (state.kind === "error") {
    return (
      <Page title="Datacrow">
        <Banner tone="critical" title="Couldn't connect to Datacrow backend">
          <p>{state.message}</p>
          <p>
            Try refreshing the page. If the problem persists, the install may
            need to be redone: uninstall from Shopify admin → Apps, then
            reinstall.
          </p>
        </Banner>
      </Page>
    );
  }

  // Routing gates, in order (see the header and gates.ts):
  //   - /data-requests             → DataRequests (review W29)
  //   - free period over, no plan  → PlanPicker mode="ended"
  //   - 0 destinations             → Onboarding (first-run wizard)
  //   - ≥1 destination + no sub    → PlanPicker (unless skipped this session)
  //   - ≥1 destination + sub       → Dashboard
  //
  // "Configured once" is sticky — even a disabled destination counts as
  // "past onboarding". The wizard isn't re-entered from settings yet;
  // that's a future feature.
  const gateInput = {
    destinationsCount: state.destinations.length,
    billing: state.billing,
    pickerSkipped,
    pickerSkippedEnded,
  };
  const gate = resolveGate({ ...gateInput, path: location.pathname });
  const backToDashboard = () => {
    navigate("/");
    // Refresh on return: billing/destination changes affect the dashboard's
    // gating + per-destination breakdown.
    void refresh(state.shop);
  };
  // The admin sidebar nav. The Data requests link shows on every screen,
  // gated ones included (review W29), so a merchant stuck at the plan picker
  // or in onboarding can still answer a customer. The other pages only show
  // once the gates pass: behind a gate they would lead back to it.
  const appNav = (full: boolean) => (
    <NavMenu>
      {/* The rel="home" link is the app's home anchor: Shopify shows the
          app name for it in the sidebar, so it is NOT rendered as its own
          item. The visible "Dashboard" link below is a separate entry. */}
      <a href="/" rel="home">
        Datacrow
      </a>
      {full && <a href="/dashboard">Dashboard</a>}
      {full && <a href="/destinations">Destinations</a>}
      {full && <a href="/billing">Manage billing</a>}
      <a href={DATA_REQUESTS_PATH}>Data requests</a>
    </NavMenu>
  );
  if (gate === "data-requests") {
    return (
      <>
        {appNav(resolveGate(gateInput) === "main")}
        <DataRequests shop={state.shop} onBack={backToDashboard} />
        <FeedbackWidget />
      </>
    );
  }
  if (gate === "picker-ended") {
    // No skip with 0 destinations: there's no dashboard worth opening.
    return (
      <>
        {appNav(false)}
        <PlanPicker
          shop={state.shop}
          planSelectionUrl={state.billing.planSelectionUrl}
          mode="ended"
          freePeriod={state.billing.freePeriod}
          canSkip={state.destinations.length > 0}
          onSkipped={skipEndedPicker}
        />
        <FeedbackWidget />
      </>
    );
  }
  if (gate === "onboarding") {
    return (
      <>
        {appNav(false)}
        <Onboarding
          shop={state.shop}
          onFinished={() => {
            // Return to the dashboard + refresh so a genuine first-run completion
            // advances past the onboarding gate.
            navigate("/");
            void refresh(state.shop);
          }}
          storeContext={state.storeContext}
        />
        <FeedbackWidget />
      </>
    );
  }
  if (gate === "picker") {
    return (
      <>
        {appNav(false)}
        <PlanPicker
          shop={state.shop}
          planSelectionUrl={state.billing.planSelectionUrl}
          mode="free"
          freePeriod={state.billing.freePeriod}
          onSkipped={() => setPickerSkipped(true)}
        />
        <FeedbackWidget />
      </>
    );
  }
  // Past the onboarding + picker gates: the main app. NavMenu renders the
  // app's navigation into Shopify admin's left sidebar (the native home for
  // embedded-app nav); the visible page is chosen by the URL-derived `view`.
  const mainContent =
    view === "billing" ? (
      <Billing shop={state.shop} onBack={backToDashboard} />
    ) : view === "destinations" ? (
      <Destinations shop={state.shop} onBack={backToDashboard} />
    ) : (
      <Dashboard
        shop={state.shop}
        destinations={state.destinations}
        availableTypes={state.availableTypes}
        entitlement={state.billing.entitlement}
        onOpenBilling={() => navigate("/billing")}
        onOpenDestinations={() => navigate("/destinations")}
        onOpenDataRequests={() => navigate(DATA_REQUESTS_PATH)}
      />
    );

  return (
    <>
      {appNav(true)}
      {refreshFailed && (
        <div style={{ maxWidth: 998, margin: "16px auto 0", padding: "0 16px" }}>
          <Banner tone="warning" title="Showing saved data">
            <p>
              Datacrow couldn't refresh, so this view is from your last visit and may be out of
              date. Reload the page to try again.
            </p>
          </Banner>
        </div>
      )}
      {/* The free-period countdown (review W31). Not on Billing, whose card
          says the same; never with a plan or a comp. After the end date the
          Dashboard's paywall banner takes over. */}
      {view !== "billing" &&
        state.billing.subscription === null &&
        !state.billing.comped &&
        state.billing.freePeriod &&
        !freePeriodEnded(state.billing) && (
          <FreePeriodBanner
            freePeriod={state.billing.freePeriod}
            planSelectionUrl={state.billing.planSelectionUrl}
          />
        )}
      {mainContent}
      <FeedbackWidget />
    </>
  );
}
